import {
    IComponentAsProps,
    INavButtonProps,
    IconButton,
    Nav,
    Stack,
    StackItem,
} from "@fluentui/react";
import { makeStyles, mergeClasses, shorthands } from "@griffel/react";
import { GoogleAnalytics } from "@next/third-parties/google";
import type { AppProps } from "next/app";
import getConfig from "next/config";
import Head from "next/head";
import Link from "next/link";
import { useRouter } from "next/router";
import { useCallback, useEffect, useState } from "react";
import { Connect, ErrorDialogProvider } from "../components";
import { useStreamingMode } from "../hooks";
import "../styles/globals.css";
import { Icons } from "../utils";
import { register as registerIcons } from "../utils/icons";

registerIcons();

const ROUTES = [
    {
        url: "/",
        icon: Icons.PhoneLaptop,
        name: "Instructions",
    },
    {
        url: "/wifi-debug",
        icon: Icons.Wifi,
        name: "Wi-Fi Debug",
    },
    {
        url: "/logcat",
        icon: Icons.BookSearch,
        name: "Logcat",
    },
    {
        url: "/mxr-logs",
        icon: Icons.Bug,
        name: "Device Logs",
    },
    {
        url: "/file-manager",
        icon: Icons.Folder,
        name: "File Manager",
    },
    {
        url: "/install",
        icon: Icons.Box,
        name: "Install APK",
    },
    {
        url: "/adb-shell",
        icon: Icons.WindowConsole,
        name: "ADB Shell",
    },
];

function NavLink({
    link,
    defaultRender: DefaultRender,
    ...props
}: IComponentAsProps<INavButtonProps>) {
    if (!link) {
        return null;
    }

    return (
        <Link href={link.url} legacyBehavior passHref>
            <DefaultRender {...props} />
        </Link>
    );
}

const useClasses = makeStyles({
    titleContainer: {
        ...shorthands.borderBottom("1px", "solid", "rgb(243, 242, 241)"),
    },
    hidden: {
        display: "none",
    },
    title: {
        // ...shorthands.padding("4px", "0"),
        fontSize: "12px",
        textAlign: "center",
    },
    leftColumn: {
        width: "270px",
        paddingRight: "8px",
        ...shorthands.borderRight("1px", "solid", "rgb(243, 242, 241)"),
        overflowY: "auto",
    },
});

const {
    publicRuntimeConfig: { basePath },
} = getConfig();

function App({ Component, pageProps }: AppProps) {
    const classes = useClasses();
    const streamingMode = useStreamingMode();

    const [leftPanelVisible, setLeftPanelVisible] = useState(false);
    const toggleLeftPanel = useCallback(() => {
        setLeftPanelVisible((value) => !value);
    }, []);
    useEffect(() => {
        setLeftPanelVisible(innerWidth > 650);
    }, []);

    const router = useRouter();

    if ("noLayout" in Component) {
        return <Component {...pageProps} />;
    }

    return (
        <ErrorDialogProvider>
            <Head>
                <link rel="manifest" href={basePath + "/manifest.json"} />
            </Head>

            <Stack verticalFill>
                <Stack
                    className={mergeClasses(
                        classes.titleContainer,
                        streamingMode === "WIRELESS" && classes.hidden,
                    )}
                    horizontal
                    verticalAlign="center"
                >
                    <IconButton
                        checked={leftPanelVisible}
                        title="Toggle Menu"
                        iconProps={{
                            iconName: Icons.Navigation,
                        }}
                        onClick={toggleLeftPanel}
                    />

                    <StackItem grow>
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "center",
                            }}
                        >
                            <img
                                style={{
                                    height: "3rem",
                                    margin: "0.5rem 0 0 0",
                                }}
                                src="/logo.png"
                            ></img>
                            <div
                                style={{
                                    height: "100%",
                                    alignSelf: "center",
                                    marginLeft: "0.7rem",
                                }}
                            >
                                (Beta)
                            </div>
                        </div>
                        {/*  */}
                    </StackItem>

                    {/* <IconButton
                        iconProps={{ iconName: "PersonFeedback" }}
                        title="Feedback"
                        as="a"
                        href="https://github.com/yume-chan/ya-webadb/issues/new"
                        target="_blank"
                    /> */}
                </Stack>

                <Stack
                    grow
                    horizontal
                    verticalFill
                    disableShrink
                    styles={{
                        root: {
                            minHeight: 0,
                            overflow: "hidden",
                            lineHeight: "1.5",
                        },
                    }}
                >
                    <StackItem
                        className={mergeClasses(
                            classes.leftColumn,
                            (!leftPanelVisible ||
                                streamingMode === "WIRELESS") &&
                                classes.hidden,
                        )}
                    >
                        <Connect />

                        <Nav
                            groups={[
                                {
                                    links: ROUTES.map((route) => ({
                                        ...route,
                                        key: route.url,
                                    })),
                                },
                            ]}
                            linkAs={NavLink}
                            selectedKey={router.pathname}
                        />
                    </StackItem>

                    <StackItem grow styles={{ root: { width: 0 } }}>
                        <Component {...pageProps} />
                    </StackItem>
                </Stack>
            </Stack>
            <GoogleAnalytics gaId="G-2QXVBP9R6Z" />
        </ErrorDialogProvider>
    );
}

export default App;
